import * as React from 'react';
import { withRouter } from 'react-router-dom';
import { NavLink } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import './user-add-or-edit.styles.scss';

import ErrorMessage from '../../components/error/error.component';
import Spinner from '../../components/spinner/spinner.component';
import GeneralConstants from '../../constants/general.constants';
import NotFound from '../not-found/not-found.component';

import api from '../../services/api.service';

const availableRoles = [
    { id: 3, label: 'sales', name: 'Selgere', is_current: false },
    { id: 4, label: 'transport', name: 'Sjåfører', is_current: false },
    { id: 5, label: 'assembly', name: 'Montører', is_current: false },
    { id: 6, label: 'project_manager', name: 'Prosjektledere', is_current: false },
    { id: 7, label: 'production', name: 'Produksjon', is_current: false },
    { id: 8, label: 'admin', name: 'Administrasjon', is_current: true }
];

class UserAddOrEdit extends React.Component {
    state = {
        user: {
            firstname: '',
            lastname: '',
            phone: '',
            email: '',
            username: '',  // Add username to initial state
            password: '',  // Only for new users
            roles: [],  // Add roles to user state
            isSuperadmin: false  // Add isSuperadmin to user state
        },
        passwordValidation: {
            minLength: false,
            hasCapital: false,
            hasNumber: false,
            isValid: false
        },
        error: null,
        errorMessage: '',
        isFetching: true,
        isSubmitting: false
    };

    async componentDidMount() {

        const userId = this.props.match.params.userId;
        
        if (userId === '0') {
            this.setState({ isFetching: false });
            return;
        }

        try {
            const user = await api.users.get(userId, 'includes=availableroles');
            this.setState({
                user: {
                    // Be spesific
                    firstname: user.firstname,
                    lastname: user.lastname,
                    phone: user.phone,
                    email: user.email,
                    username: user.username, // Add username
                    roles: user.availableroles.map(role => role.label),
                    isSuperadmin: user.isAdmin ?? false,
                    password: ''  // Don't show existing password
                },
                isFetching: false
            });
        } catch (error) {
            this.setState({
                isFetching: false,
                error,
                errorMessage: true
            });
        }
    }

    validatePassword = (password) => {
        const validations = {
            minLength: password.length >= 6,
            hasCapital: /[A-Z]/.test(password),
            hasNumber: /[0-9]/.test(password),
        };
        
        return {
            ...validations,
            isValid: Object.values(validations).every(Boolean)
        };
    };

    handleInputChange = (event) => {
        const { name, value, type, checked } = event.target;
        const inputValue = type === 'checkbox' ? checked : value;
        this.setState(prevState => ({
            user: {
                ...prevState.user,
                [name]: inputValue
            },
            ...(name === 'password' && {
                passwordValidation: this.validatePassword(value)
            })
        }));
    };

    handleRolesChange = (event) => {
        const { options } = event.target;
        const roles = [];
        for (let i = 0, l = options.length; i < l; i++) {
            if (options[i].selected) {
                roles.push(options[i].value);
            }
        }
        this.setState(prevState => ({
            user: {
                ...prevState.user,
                roles
            }
        }));
    };

    handleSubmit = async (event) => {
        event.preventDefault();
        this.setState({ isSubmitting: true });

        const userId = this.props.match.params.userId;
        const { user } = this.state;

        // Map role labels to role IDs
        const roleIds = user.roles.map(roleLabel => {
            const role = availableRoles.find(r => r.label === roleLabel);
            return role ? role.id : null;
        }).filter(id => id !== null);

        // Add superadmin role ID if isSuperadmin is checked
        if (user.isSuperadmin) {
            roleIds.push(1);
        }

        // Prepare data for API
        const userData = {
            firstname: user.firstname,
            lastname: user.lastname,
            phone: user.phone,
            email: user.email,
            username: user.username,
            roles: roleIds
        };

        // Only include password if it's provided
        if (user.password) {
            userData.password = user.password;
        }

        try {
            if (userId === '0') {
                await api.users.post(userData);
            } else {
                userData.id = userId;
                await api.users.put(userId, userData);
            }
            this.props.history.push('/admin-user');
        } catch (error) {
            console.error(error);
            this.setState({
                error,
                errorMessage: true,
                isSubmitting: false
            });
        }
    };

    handleDelete = async () => {
        const userId = this.props.match.params.userId;
        
        // Show confirmation dialog
        const isConfirmed = window.confirm('Er du sikker på at du vil slette denne brukeren? Dette kan ikke angres.');
        
        if (isConfirmed) {
            this.setState({ isSubmitting: true });
            try {
                await api.users.delete(userId);
                this.props.history.push('/admin-user');
            } catch (error) {
                console.error(error);
                this.setState({
                    error,
                    errorMessage: true,
                    isSubmitting: false
                });
            }
        }
    };

    render() {
        const { isFetching, isSubmitting, errorMessage, user, passwordValidation } = this.state;
        const userId = this.props.match.params.userId;
        const isNewUser = userId === '0';

        return (

            <div className='project-data-container'>

                <nav className='project-nav'>
                    <NavLink to='/' className='project-nav--home' />
                    <NavLink to='/admin-user' className='admin-nav--user' activeClassName='admin-nav--active'>
                        {GeneralConstants['edit_users']}
                    </NavLink>
                    <NavLink to='/admin-form' className='admin-nav--form' activeClassName='admin-nav--active'>
                        {GeneralConstants['edit_form_data']}
                    </NavLink>
                </nav>

                {isFetching && (
                    <CSSTransition in={isFetching} appear timeout={{ appear: 300, exit: 150 }}>
                        <Spinner />
                    </CSSTransition>
                )}

                {this.state.error?.http_code === 404 ? (
                    <NotFound />
                ) : (
                    errorMessage && (
                        <ErrorMessage errorID={this.state.error?.id || this.state.error?.status} />
                    )
                )}

                <div className='title-container'>
                    <h1>{isNewUser ? 'Opprett ny bruker' : 'Rediger bruker'}</h1>
                </div>

                <form onSubmit={this.handleSubmit} className="user-form" autoComplete="off">
                    <div className="form-group">
                        <label htmlFor="firstname">{GeneralConstants['first_name']}</label>
                        <input
                            type="text"
                            id="firstname"
                            name="firstname"
                            value={user.firstname}
                            onChange={this.handleInputChange}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="lastname">{GeneralConstants['last_name']}</label>
                        <input
                            type="text"
                            id="lastname"
                            name="lastname"
                            value={user.lastname}
                            onChange={this.handleInputChange}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="phone">{GeneralConstants['phone']}</label>
                        <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={user.phone}
                            onChange={this.handleInputChange}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="email">{GeneralConstants['email']}</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={user.email}
                            onChange={this.handleInputChange}
                            required
                            autoComplete="new-email"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="username">Brukernavn</label>
                        <input
                            type="text"
                            id="username"
                            name="username"
                            value={user.username}
                            onChange={this.handleInputChange}
                            required
                            autoComplete="new-username"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="password">
                            {isNewUser ? 'Passord' : 'Nytt passord (La stå tomt for å beholde eksisterende)'}
                        </label>
                        <input
                            type="password"
                            id="password"
                            name="password"
                            value={user.password}
                            onChange={this.handleInputChange}
                            required={isNewUser}
                            autoComplete="new-password"
                        />
                        {user.password && (
                            <div className="password-requirements">
                                <p className={passwordValidation.minLength ? 'valid' : 'invalid'}>
                                    ⚬ Minimum 6 tegn
                                </p>
                                <p className={passwordValidation.hasCapital ? 'valid' : 'invalid'}>
                                    ⚬ Minst én stor bokstav
                                </p>
                                <p className={passwordValidation.hasNumber ? 'valid' : 'invalid'}>
                                    ⚬ Minst ett tall
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="form-group">
                        <label htmlFor="roles">Roller</label>
                        <select
                            id="roles"
                            name="roles"
                            multiple
                            value={user.roles}
                            onChange={this.handleRolesChange}
                        >
                            {availableRoles.map(role => (
                                <option key={role.id} value={role.label}>{role.name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="checkbox-group">
                        <input
                            type="checkbox"
                            id="isSuperadmin"
                            name="isSuperadmin"
                            checked={user.isSuperadmin}
                            onChange={this.handleInputChange}
                        />
                        <label htmlFor="isSuperadmin" style={{ display: 'inline', marginLeft: '8px' }}>
                            Er superadmin
                        </label>
                    </div>

                    <div className="button-group">
                        <button 
                            type="submit" 
                            className="btn btn-primary"
                            disabled={isSubmitting || (user.password && !passwordValidation.isValid)}
                        >
                            {isSubmitting ? 'Lagrer...' : 'Lagre'}
                        </button>
                        <button 
                            type="button" 
                            className="btn btn-secondary"
                            onClick={() => this.props.history.push('/admin-user')}
                        >
                            Avbryt
                        </button>
                        {!isNewUser && (
                            <button 
                                type="button" 
                                className="btn btn-danger"
                                onClick={this.handleDelete}
                                disabled={isSubmitting}
                                style={{ backgroundColor: "#dc3545", color: "white", borderColor: "#dc3545" }}
                            >
                                {isSubmitting ? 'Sletter...' : 'Slett bruker'}
                            </button>
                        )}
                    </div>
                </form>
            </div>
        );
    }
}

export default withRouter(UserAddOrEdit);
